import axios from "axios";
import { CalendarIcon } from "lucide-react";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FaBuilding,
  FaUser,
  FaBriefcase,
  FaTriangleExclamation,
  FaFileSignature,
  FaUpload,
  FaXmark,
  FaClipboardCheck,
} from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../payslip/payslippicker.css";

const steps = [
  {
    id: 1,
    title: "Company\nInformation",
    mobileTitle: "Company\nInformation",
    icon: FaBuilding,
    fields: [
      "companyName",
      "companyEmail",
      "companyPhone",
      "companyWebsite",
      "companyAddress",
      "companyLogo",
    ],
  },
  {
    id: 2,
    title: "Employee\nInformation",
    mobileTitle: "Employee\nInformation",
    icon: FaUser,
    fields: [
      "employeeName",
      "employeeId",
      "employeePhone",
      "employeeEmail",
      "residentialAddress",
      "currentDesignation",
      "currentDepartment",
      "workLocation",
      "reportingManager",
    ],
  },
  {
    id: 3,
    title: "Warning\nDetails",
    mobileTitle: "Warning\nDetails",
    icon: FaTriangleExclamation,
    fields: [
      "warningLetterDate",
      "warningLevel",
      "warningCategory",
      "severityLevel",
      "previousWarningIssued",
      "previousWarningDate",
      "previousWarningReference",
    ],
  },
  {
    id: 4,
    title: "Incident\nInformation",
    mobileTitle: "Incident\nInformation",
    icon: FaClipboardCheck,
    fields: [
      "incidentDate",
      "incidentTime",
      "incidentLocation",
      "policyViolated",
      "incidentDescription",
      "witnessDetails",
      "employeeExplanation",
      "employeeResponseSummary",
    ],
  },
  {
    id: 5,
    title: "Corrective\nAction",
    mobileTitle: "Corrective\nAction",
    icon: FaBriefcase,
    fields: [
      "concernDescription",
      "expectedStandard",
      "requiredImprovement",
      "correctiveActionPlan",
      "improvementDeadline",
      "reviewDate",
      "monitoringPeriod",
      "trainingSupportProvided",
    ],
  },
  {
    id: 6,
    title: "Consequences\n& Authorisation",
    mobileTitle: "Consequences\n& Authorisation",
    icon: FaFileSignature,
    fields: [
      "consequenceOfNonImprovement",
      "meetingDetails",
      "employeeResponseRequired",
      "responseDeadline",
      "issuedBy",
      "approvedBy",
      "referenceNumber",
      "acknowledgementRequired",
    ],
  },
];

const defaultValues = {
  // Company
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  companyAddress: "",
  companyLogo: null,

  // Employee
  employeeName: "",
  employeeId: "",
  employeePhone: "",
  employeeEmail: "",
  residentialAddress: "",
  currentDesignation: "",
  currentDepartment: "",
  workLocation: "",
  reportingManager: "",

  // Warning Details
  warningLetterDate: "",
  warningLevel: "",
  warningCategory: "",
  severityLevel: "",
  previousWarningIssued: "",
  previousWarningDate: "",
  previousWarningReference: "",

  // Incident Information
  incidentDate: "",
  incidentTime: "",
  incidentLocation: "",
  policyViolated: "",
  incidentDescription: "",
  witnessDetails: "",
  employeeExplanation: "",
  employeeResponseSummary: "",

  // Corrective Action
  concernDescription: "",
  expectedStandard: "",
  requiredImprovement: "",
  correctiveActionPlan: "",
  improvementDeadline: "",
  reviewDate: "",
  monitoringPeriod: "",
  trainingSupportProvided: "",

  // Consequences and Authorisation
  consequenceOfNonImprovement: "",
  meetingDetails: "",
  employeeResponseRequired: "",
  responseDeadline: "",
  issuedBy: "",
  approvedBy: "",
  referenceNumber: "",
  acknowledgementRequired: "",
};

const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0";

const labelClass = "mb-1.5 block text-sm font-medium text-[#10324F]";

const FormField = ({ label, required, error, children }) => {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-[10px] text-red-500">{error.message}</p>
      )}
    </div>
  );
};

const parseDateValue = (value) => {
  if (!value) return null;

  const [year, month, day] = String(value).split("-").map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const formatDateValue = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const DateField = ({ name, label, required, control, errors }) => (
  <FormField label={label} required={required} error={errors[name]}>
    <Controller
      name={name}
      control={control}
      rules={
        required
          ? {
              required: `${label} is required`,
            }
          : {}
      }
      render={({ field }) => (
        <DatePicker
          selected={parseDateValue(field.value)}
          onChange={(date) => {
            field.onChange(formatDateValue(date));
          }}
          onBlur={field.onBlur}
          dateFormat="yyyy-MM-dd"
          showPopperArrow={false}
          isClearable
          placeholderText="Select date"
          showIcon
          toggleCalendarOnIconClick
          icon={<CalendarIcon className="h-4 w-4" />}
          className={`${inputClass} h-10`}
          wrapperClassName="w-full"
          popperClassName="payslip-datepicker-popper react-popper-offset"
          calendarClassName="payslip-datepicker"
          popperPlacement="top-start"
          portalId="datepicker-portal"
        />
      )}
    />
  </FormField>
);

const BASE_URL = import.meta.env.VITE_BASE_URL;

const WarningLetterGeneratorForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState("");
  const [evidenceName, setEvidenceName] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const values = watch();

  const currentStep = steps[activeStep];

  const handleNext = async () => {
    const valid = await trigger(currentStep.fields);

    // Company logo is manually handled
    if (activeStep === 0 && !logoPreview) {
      setError("companyLogo", {
        type: "required",
        message: "Company logo is required",
      });

      return;
    }

    if (!valid) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    clearErrors("companyLogo");
  };

  const clearLogo = () => {
    setLogoPreview("");
    setValue("companyLogo", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit = async (data) => {
    try {
      if (isGenerating) return;

      setIsGenerating(true);

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "companyLogo") {
          if (value instanceof File) {
            formData.append(key, value);
          }
        } else {
          formData.append(key, value ?? "");
        }
      });

      const response = await axios.post(
        `${BASE_URL}/generate-warningletter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      const contentType = response.headers["content-type"];

      if (!contentType?.includes("application/pdf")) {
        const text = await response.data.text();

        console.error("Backend returned:", text);

        throw new Error("Backend did not return a Warning Letter PDF");
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Warning-Letter-${data.employeeName || "Employee"}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.open(url, "_blank");

      //  reset(defaultValues);

      setLogoPreview("");

      setEvidenceName("");

      setActiveStep(0);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("Warning letter generation failed:", error);

      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();

        try {
          const errorData = JSON.parse(text);

          alert(errorData?.message || "Failed to generate Warning Letter");
        } catch {
          alert("Failed to generate Warning Letter");
        }
      } else {
        alert(error?.message || "Failed to generate Warning Letter");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section>
      <form className="w-full">
        <div className="mx-auto grid w-full grid-cols-1 gap-2 xl:grid-cols-[1.9fr_1fr]">
          {/* ================= FORM ================= */}
          <div className="min-w-0 rounded-xl border border-white  shadow-sm backdrop-blur-2xl p-4">
            {/* STEP NAVIGATION */}
            <div className="scrollbar-hide flex w-full gap-2 overflow-x-auto pb-2 sm:overflow-visible">
              {steps.map((step, index) => {
                const active = activeStep === index;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={async () => {
                      if (index <= activeStep) {
                        setActiveStep(index);
                        return;
                      }

                      const valid = await trigger(steps[activeStep].fields);

                      if (valid) {
                        setActiveStep(index);
                      }
                    }}
                    className={`flex min-w-26.25 shrink-0 flex-1 items-center justify-center rounded-md px-2 py-2.5 text-center transition                       
          sm:min-w-0 
          ${
            active
              ? " bg-[#10324F] text-white shadow-sm"
              : "border border-white  text-[#10324F] bg-linear-to-b from-white/60 to-[#D1DFEA]/30  shadow-sm hover:bg-white/70"
          }`}
                  >
                    <span className="whitespace-pre-line text-xs leading-4 sm:text-sm">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CURRENT STEP */}
            <div className="mt-5">
              {activeStep === 0 && (
                <CompanyInformation
                  register={register}
                  errors={errors}
                  handleLogoChange={handleLogoChange}
                  logoPreview={logoPreview}
                  clearLogo={clearLogo}
                />
              )}

              {activeStep === 1 && (
                <EmployeeInformation register={register} errors={errors} />
              )}

              {activeStep === 2 && (
                <WarningDetails
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 3 && (
                <IncidentInformation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 4 && (
                <CorrectiveAction
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 5 && (
                <ConsequencesAuthorisation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}
            </div>

            {/* NAVIGATION BUTTONS */}
            <div className="mt-7 flex items-center justify-center gap-3">
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-30 rounded-md border border-[#163957] px-6 py-2.5 text-sm font-medium text-[#163957] transition hover:bg-[#163957] hover:text-white"
                >
                  Previous
                </button>
              )}

              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-30 rounded-md bg-[#163957] px-8 py-2.5 text-sm font-medium text-white transition hover:bg-[#10324F]"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleSubmit(onSubmit)}
                  className={`min-w-45 rounded-md px-8 py-2.5 text-sm font-medium text-white transition ${
                    isGenerating
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-[#10324F] hover:bg-[#10324F]"
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </span>
                  ) : (
                    "Generate Warning Letter"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ================= PREVIEW ================= */}
          <WarningLetterPreview
            values={values}
            logoPreview={logoPreview}
            evidenceName={evidenceName}
          />
        </div>
      </form>
    </section>
  );
};

export default WarningLetterGeneratorForm;

const CompanyInformation = ({
  register,
  errors,
  handleLogoChange,
  logoPreview,
  clearLogo,
}) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Company Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Company Name" required error={errors.companyName}>
          <input
            {...register("companyName", {
              required: "Company name is required",
              minLength: {
                value: 2,
                message: "Enter a valid company name",
              },
            })}
            className={inputClass}
            placeholder="Enter Company name"
          />
        </FormField>

        <FormField label="Company Email" required error={errors.companyEmail}>
          <input
            type="email"
            {...register("companyEmail", {
              required: "Company email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Email"
          />
        </FormField>

        <FormField label="Phone Number" required error={errors.companyPhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("companyPhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Website" error={errors.companyWebsite}>
          <input
            {...register("companyWebsite", {
              required: "Company website is required",
              pattern: {
                value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
                message: "Enter valid website",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Website"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Company Address"
            required
            error={errors.companyAddress}
          >
            <input
              {...register("companyAddress", {
                required: "Company address is required",
              })}
              className={inputClass}
              placeholder="Enter Company Address"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField label="Company Logo" required error={errors.companyLogo}>
            {logoPreview ? (
              <div className="w-60">
                <div className="relative flex h-16 w-full items-center justify-center rounded-md border border-slate-200 bg-white">
                  <img
                    src={logoPreview}
                    alt="Company logo"
                    className="h-14 max-w-55 object-contain"
                  />

                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
                    aria-label="Remove logo"
                  >
                    <FaXmark className="text-xs" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex h-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white transition hover:bg-slate-50">
                <div className="text-center text-[10px] text-[#10324F]">
                  <FaUpload className="mx-auto mb-1 text-[#10324F]/30" />
                  Upload Logo
                </div>

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </FormField>
        </div>
      </div>
    </div>
  );
};

const EmployeeInformation = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Employee Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Employee Full Name"
          required
          error={errors.employeeName}
        >
          <input
            {...register("employeeName", {
              required: "Employee name is required",
            })}
            className={inputClass}
            placeholder="Enter name"
          />
        </FormField>

        <FormField label="Employee ID" required error={errors.employeeId}>
          <input
            {...register("employeeId", {
              required: "Employee ID is required",
            })}
            className={inputClass}
            placeholder="Enter ID"
          />
        </FormField>

        <FormField label="Phone Number" error={errors.employeePhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("employeePhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Email Address" error={errors.employeeEmail}>
          <input
            type="email"
            {...register("employeeEmail", {
              required: "Employee email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Email"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Residential address"
            required
            error={errors.residentialAddress}
          >
            <input
              {...register("residentialAddress", {
                required: "Residential address is required",
              })}
              className={inputClass}
              placeholder="Enter address"
            />
          </FormField>
        </div>

        <FormField
          label="Current designation"
          required
          error={errors.currentDesignation}
        >
          <input
            {...register("currentDesignation", {
              required: "Current designation is required",
            })}
            className={inputClass}
            placeholder="Enter Final designation"
          />
        </FormField>

        <FormField label="Current Department" error={errors.currentDepartment}>
          <input
            {...register("currentDepartment")}
            className={inputClass}
            placeholder="Enter Department"
          />
        </FormField>

        <FormField label="Work Location" error={errors.workLocation}>
          <input
            {...register("workLocation")}
            className={inputClass}
            placeholder="Enter Work Location"
          />
        </FormField>

        <FormField
          label="Current reporting manager"
          error={errors.reportingManager}
        >
          <input
            {...register("reportingManager")}
            className={inputClass}
            placeholder="Enter Reporting manager"
          />
        </FormField>
      </div>
    </div>
  );
};

const WarningDetails = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Warning Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <DateField
            name="warningLetterDate"
            label="Warning letter date"
            required
            control={control}
            errors={errors}
          />
        </div>

        <FormField label="Warning level" required error={errors.warningLevel}>
          <select
            {...register("warningLevel", {
              required: "Warning level is required",
            })}
            className={inputClass}
          >
            <option value="">Select Warning level</option>
            <option value="Verbal Warning (Documented)">
              Verbal Warning (Documented)
            </option>
            <option value="First Written Warning">First Written Warning</option>
            <option value="Second Written Warning">
              Second Written Warning
            </option>
            <option value="Final Written Warning">Final Written Warning</option>
          </select>
        </FormField>

        <FormField
          label="Warning category"
          required
          error={errors.warningCategory}
        >
          <select
            {...register("warningCategory", {
              required: "Warning category is required",
            })}
            className={inputClass}
          >
            <option value="">Select Warning category</option>
            <option value="Attendance">Attendance</option>
            <option value="Punctuality">Punctuality</option>
            <option value="Performance">Performance</option>
            <option value="Misconduct">Misconduct</option>
            <option value="Insubordination">Insubordination</option>
            <option value="Policy Violation">Policy Violation</option>
            <option value="Workplace Behaviour">Workplace Behaviour</option>
            <option value="Other">Other</option>
          </select>
        </FormField>

        <FormField label="Severity level" required error={errors.severityLevel}>
          <select
            {...register("severityLevel", {
              required: "Severity level is required",
            })}
            className={inputClass}
          >
            <option value="">Select Severity level</option>
            <option value="Minor">Minor</option>
            <option value="Moderate">Moderate</option>
            <option value="Serious">Serious</option>
            <option value="Critical">Critical</option>
          </select>
        </FormField>

        <FormField
          label="Previous warning issued"
          error={errors.previousWarningIssued}
        >
          <select {...register("previousWarningIssued")} className={inputClass}>
            <option value="">Select option</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </FormField>

        <DateField
          name="previousWarningDate"
          label="Previous warning date"
          control={control}
          errors={errors}
        />

        <FormField
          label="Previous warning reference number"
          error={errors.previousWarningReference}
        >
          <input
            {...register("previousWarningReference")}
            className={inputClass}
            placeholder="Enter reference number"
          />
        </FormField>
      </div>
    </div>
  );
};

const IncidentInformation = ({
  register,
  errors,
  control,
  handleEvidenceChange,
  evidenceName,
  clearEvidence,
}) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Incident Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          name="incidentDate"
          label="Incident date"
          required
          control={control}
          errors={errors}
        />

        <FormField label="Incident time" error={errors.incidentTime}>
          <input
            type="time"
            {...register("incidentTime")}
            className={inputClass}
            placeholder="HH:MM"
          />
        </FormField>

        <FormField label="Incident location" error={errors.incidentLocation}>
          <input
            {...register("incidentLocation")}
            className={inputClass}
            placeholder="Enter Incident location"
          />
        </FormField>

        <FormField
          label="Policy or rule violated"
          error={errors.policyViolated}
        >
          <select {...register("policyViolated")} className={inputClass}>
            <option value="">Select Policy or rule</option>
            <option value="Attendance Policy">Attendance Policy</option>
            <option value="Leave Policy">Leave Policy</option>
            <option value="Code of Conduct">Code of Conduct</option>
            <option value="Data Security Policy">Data Security Policy</option>
            <option value="Workplace Safety Policy">
              Workplace Safety Policy
            </option>
            <option value="Anti Harassment Policy">
              Anti Harassment Policy
            </option>
            <option value="Other">Other</option>
          </select>
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Detailed incident description"
            required
            error={errors.incidentDescription}
          >
            <textarea
              {...register("incidentDescription", {
                required: "Incident description is required",
              })}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter Detailed incident description"
            />
          </FormField>

          <FormField label="Witness details" error={errors.witnessDetails}>
            <input
              {...register("witnessDetails")}
              className={inputClass}
              placeholder="Enter Witness details"
            />
          </FormField>
        </div>
        <FormField
          label="Employee explanation"
          error={errors.employeeExplanation}
        >
          <textarea
            {...register("employeeExplanation")}
            rows={3}
            className={`${inputClass} resize-none`}
            placeholder="Enter Employee explanation"
          />
        </FormField>

        <FormField
          label="Employee response summary"
          error={errors.employeeResponseSummary}
        >
          <textarea
            {...register("employeeResponseSummary")}
            rows={3}
            className={`${inputClass} resize-none`}
            placeholder="Enter Employee response summary"
          />
        </FormField>
      </div>
    </div>
  );
};

const CorrectiveAction = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Corrective Action
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Description of the concern"
          required
          error={errors.concernDescription}
        >
          <input
            {...register("concernDescription", {
              required: "Description of the concern is required",
            })}
            className={inputClass}
            placeholder="Enter Description of the concern"
          />
        </FormField>

        <FormField
          label="Expected standard"
          required
          error={errors.expectedStandard}
        >
          <input
            {...register("expectedStandard", {
              required: "Expected standard is required",
            })}
            className={inputClass}
            placeholder="Enter Expected standard"
          />
        </FormField>

        <FormField
          label="Required improvement"
          required
          error={errors.requiredImprovement}
        >
          <input
            {...register("requiredImprovement", {
              required: "Required improvement is required",
            })}
            className={inputClass}
            placeholder="Enter Required improvement"
          />
        </FormField>

        <FormField
          label="Corrective action plan"
          error={errors.correctiveActionPlan}
        >
          <input
            {...register("correctiveActionPlan")}
            className={inputClass}
            placeholder="Enter Corrective action plan"
          />
        </FormField>

        <DateField
          name="improvementDeadline"
          label="Improvement deadline"
          required
          control={control}
          errors={errors}
        />

        <DateField
          name="reviewDate"
          label="Review date"
          required
          control={control}
          errors={errors}
        />

        <FormField label="Monitoring period" error={errors.monitoringPeriod}>
          <select {...register("monitoringPeriod")} className={inputClass}>
            <option value="">Select Monitoring period</option>
            <option value="15 Days">15 Days</option>
            <option value="30 Days">30 Days</option>
            <option value="60 Days">60 Days</option>
            <option value="90 Days">90 Days</option>
          </select>
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Training or support provided"
            error={errors.trainingSupportProvided}
          >
            <textarea
              {...register("trainingSupportProvided")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter Training or support provided"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const ConsequencesAuthorisation = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Consequences and Authorisation
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Consequence of non-improvement"
          required
          error={errors.consequenceOfNonImprovement}
        >
          <input
            {...register("consequenceOfNonImprovement", {
              required: "Consequence of non-improvement is required",
            })}
            className={inputClass}
            placeholder="Enter Consequence of non-improvement"
          />
        </FormField>

        <FormField label="Meeting details" error={errors.meetingDetails}>
          <input
            {...register("meetingDetails")}
            className={inputClass}
            placeholder="Enter Meeting details"
          />
        </FormField>

        <FormField
          label="Employee response required"
          error={errors.employeeResponseRequired}
        >
          <select
            {...register("employeeResponseRequired")}
            className={inputClass}
          >
            <option value="">Yes / No</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </FormField>

        <DateField
          name="responseDeadline"
          label="Response deadline"
          control={control}
          errors={errors}
        />

        <FormField label="Issued by" required error={errors.issuedBy}>
          <input
            {...register("issuedBy", {
              required: "Issued by is required",
            })}
            className={inputClass}
            placeholder="Enter name & Designation"
          />
        </FormField>

        <FormField label="Approved by" error={errors.approvedBy}>
          <input
            {...register("approvedBy")}
            className={inputClass}
            placeholder="Enter name & Designation"
          />
        </FormField>

        <FormField label="Reference number" error={errors.referenceNumber}>
          <input
            {...register("referenceNumber")}
            className={inputClass}
            placeholder="Enter Reference number"
          />
        </FormField>

        <FormField
          label="Employee acknowledgement required"
          error={errors.acknowledgementRequired}
        >
          <select
            {...register("acknowledgementRequired")}
            className={inputClass}
          >
            <option value="">Yes / No</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </FormField>
      </div>
    </div>
  );
};

const WarningLetterPreview = ({ values, logoPreview, evidenceName }) => {
  const formatDate = (date) => {
    if (!date) return "24 August, 2026";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return date;

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-xl border border-white shadow-sm backdrop-blur-2xl p-4">
      <h2 className="mb-5 text-sm font-semibold text-[#10324F]">Preview</h2>

      <div className="mx-auto w-full bg-white">
        <Ribbon position="top" />

        <div className="px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company"
                className="h-8 max-w-25 object-contain"
              />
            ) : (
              <span className="text-lg text-[#10324F]">
                {values.companyName || "Company"}
              </span>
            )}
          </div>

          {/* Heading */}
          <div className="mt-3 text-center">
            <p className="text-xs font-bold text-[#10324F]">WARNING LETTER</p>
          </div>

          {/* Reference */}
          {values.referenceNumber && (
            <div className="mt-333333333 text-xs text-[#10324F]">
              <p>
                <strong>Reference No:</strong> {values.referenceNumber}
              </p>
            </div>
          )}

          {/* Recipient */}
          <div className="mt-7 text-xs leading-4 text-[#10324F]">
            <p className="font-semibold">To:</p>

            <p>{values.employeeName || "Employee Name"}</p>

            {values.employeeId && <p>Employee ID: {values.employeeId}</p>}

            <p>{values.residentialAddress || "Employee Address"}</p>
          </div>

          {/* Body */}
          <div className="mt-4 text-xs leading-4 text-[#10324F]">
            <p>Dear {values.employeeName || "Employee Name"},</p>

            <p className="mt-3">
              This letter is issued as a{" "}
              <strong>{values.warningLevel || "Written Warning"}</strong>
              {values.warningCategory && (
                <>
                  {" "}
                  in relation to <strong>{values.warningCategory}</strong>
                </>
              )}
              {values.severityLevel && (
                <>
                  . The matter has been assessed as{" "}
                  <strong>{values.severityLevel}</strong>
                </>
              )}
              .
            </p>

            <p className="mt-2">
              An incident was recorded on{" "}
              <strong>{formatDate(values.incidentDate)}</strong>
              {values.incidentTime && <> at {values.incidentTime}</>}
              {values.incidentLocation && <> at {values.incidentLocation}</>}
              {values.policyViolated && (
                <>
                  , involving a violation of the{" "}
                  <strong>{values.policyViolated}</strong>
                </>
              )}
              .
            </p>

            {values.incidentDescription && (
              <p className="mt-2">{values.incidentDescription}</p>
            )}

            {values.witnessDetails && (
              <p className="mt-2">Witness: {values.witnessDetails}</p>
            )}

            {evidenceName && (
              <p className="mt-2">Supporting evidence: {evidenceName}</p>
            )}

            {values.employeeExplanation && (
              <p className="mt-2">{values.employeeExplanation}</p>
            )}

            {values.previousWarningIssued === "Yes" && (
              <p className="mt-2">
                A previous warning was issued on{" "}
                <strong>{formatDate(values.previousWarningDate)}</strong>
                {values.previousWarningReference && (
                  <> (Ref: {values.previousWarningReference})</>
                )}
                .
              </p>
            )}

            {values.concernDescription && (
              <p className="mt-2">{values.concernDescription}</p>
            )}

            {values.expectedStandard && (
              <p className="mt-2">
                Expected standard: {values.expectedStandard}
              </p>
            )}

            {values.requiredImprovement && (
              <p className="mt-2">
                Required improvement: {values.requiredImprovement}
              </p>
            )}

            {values.improvementDeadline && (
              <p className="mt-2">
                Improvement deadline:{" "}
                <strong>{formatDate(values.improvementDeadline)}</strong>
                {values.reviewDate && (
                  <> | Review date: {formatDate(values.reviewDate)}</>
                )}
                {values.monitoringPeriod && (
                  <> | Monitoring period: {values.monitoringPeriod}</>
                )}
              </p>
            )}

            {values.consequenceOfNonImprovement && (
              <p className="mt-2">
                Failure to improve may result in{" "}
                <strong>{values.consequenceOfNonImprovement}</strong>.
              </p>
            )}

            {values.employeeResponseRequired === "Yes" && (
              <p className="mt-2">
                You are required to submit a written response
                {values.responseDeadline && (
                  <> by {formatDate(values.responseDeadline)}</>
                )}
                .
              </p>
            )}
          </div>

          {/* Date */}
          <div className="mt-4 text-xs text-[#10324F]">
            <p>Date: {formatDate(values.warningLetterDate)}</p>
          </div>

          {/* Signature */}
          <div className="mt-5 text-xs text-[#10324F]">
            <p>Sincerely,</p>

            <p className="mt-3 font-semibold">
              {values.issuedBy || "Authorised Signatory"}
            </p>

            {values.approvedBy && <p>Approved by: {values.approvedBy}</p>}

            <p>{values.companyName || "Company Name"}</p>
          </div>

          {/* Acknowledgement */}
          {values.acknowledgementRequired === "Yes" && (
            <div className="mt-4 text-xs leading-4 text-[#10324F]">
              <p className="font-semibold">Employee Acknowledgement</p>

              <p className="mt-1">
                I acknowledge receipt of this warning letter and understand its
                contents.
              </p>

              <p className="mt-3">Signature: ____________ Date: ____________</p>
            </div>
          )}

          <div className="mt-6 text-center text-xs text-[#10324F]">
            Powered by Waggex
          </div>
        </div>

        <Ribbon position="bottom" />
      </div>
    </div>
  );
};

const Ribbon = ({ position = "top" }) => {
  const isTop = position === "top";

  return (
    <div className="relative -mx-px h-9.25 w-[calc(100%+2px)] overflow-hidden">
      {/* Green base */}
      <div className="absolute -inset-0.5 bg-[#10324F]" />

      <div
        className="absolute -inset-0.5"
        style={{
          transform: isTop ? "none" : "scaleX(-1) scaleY(-1)",
        }}
      >
        {/* Magenta ribbon */}
        <div
          className="absolute inset-0 bg-[#D1DFEA]"
          style={{
            clipPath:
              "polygon(76% 0%, 100% 0%, 100% 100%, 51% 100%, 47% 60%, 70% 55%)",
          }}
        />

        {/* White cut / separation */}
        <div
          className="absolute bottom-0 left-0 h-4 w-[55%] bg-white"
          style={{
            clipPath: "polygon(0 0, 88% 0, 100% 100%, 0 100%)",
          }}
        />
      </div>
    </div>
  );
};
